import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns'
import { forkJoin, from, map, Observable, switchMap, tap } from 'rxjs'

import {
  Environment,
  ExpandedHuntPackageRecord,
  HuntPackageRecord,
  HuntPackagesAdded,
  HuntSummary,
  HUNT_PACKAGE_MAX,
} from '@cyborg-monorepo/hunter/common'
import { BaseDialogComponent } from '@hyperkit'

import { isHuntPackageHuntCompatible } from '../../../../../helpers/utils'
import { HuntAPIService } from '../../../../hunt/services/api/hunt-api.service'
import { ContentService } from '../../../services/content.service'

@Component({
  standalone: false,
  selector: 'add-to-quick-hunt-dialog',
  templateUrl: './add-to-quick-hunt-dialog.component.html',
  styleUrls: ['./add-to-quick-hunt-dialog.component.scss'],
})
export class AddToQuickHuntDialogComponent
  extends BaseDialogComponent<
    { huntPackages: HuntPackageRecord[] },
    HuntPackagesAdded
  >
  implements OnInit, AfterViewInit
{
  huntService = inject(HuntAPIService)
  contentService = inject(ContentService)

  public generatedHuntPackages: ExpandedHuntPackageRecord[]
  public static count = 0
  public error: string
  public hunt = new FormControl()
  public hunts: Array<HuntSummary>
  public huntData: Array<HuntSummary>
  @ViewChild('combo') public combo: ComboBoxComponent

  public ngOnInit(): void {
    this.huntService.getQuickHunts().subscribe((hunts: Array<HuntSummary>) => {
      this.huntData = this.hunts = hunts
    })
    AddToQuickHuntDialogComponent.count = this.data.huntPackages.filter(
      (huntPackage) => {
        return isHuntPackageHuntCompatible(huntPackage)
      },
    ).length
  }

  public ngAfterViewInit(): void {
    const contains =
      (value) =>
      (s: HuntSummary): boolean =>
        s.name.toLowerCase().indexOf(value.toLowerCase()) !== -1

    this.combo.filterChange
      .asObservable()
      .pipe(
        switchMap((value) =>
          from([this.hunts]).pipe(
            tap(() => (this.combo.loading = true)),
            map((data) => data.filter(contains(value))),
          ),
        ),
      )
      .subscribe((x) => {
        this.huntData = x
        this.combo.loading = false
      })
  }

  public onSelectionChange(event): void {
    const generatedHuntPackages: Observable<ExpandedHuntPackageRecord>[] =
      this.data.huntPackages
        .filter((huntPackage) => {
          return isHuntPackageHuntCompatible(huntPackage)
        })
        .map((huntPackage) => {
          return this.contentService
            .getHuntPackageContent(huntPackage.UUID, {
              environments: event.environments.map((env) => env.id),
            })
            .pipe(
              map((generatedContent: Environment[]) => {
                generatedContent = generatedContent.map((env) => {
                  env.tools = env.tools.filter((tool) => {
                    return (
                      tool.generatedContent &&
                      tool.generatedContent?.Hunt?.length > 0
                    )
                  })
                  return env
                })

                const expanded = huntPackage as ExpandedHuntPackageRecord
                expanded.analysis = expanded.originalAnalysis = generatedContent
                return expanded
              }),
            )
        })
    forkJoin(generatedHuntPackages).subscribe((generatedHuntPacks) => {
      this.generatedHuntPackages = generatedHuntPacks
    })
  }

  public itemDisabled(itemArgs: {
    dataItem: HuntSummary
    index: number
  }): boolean {
    return (
      itemArgs.dataItem.numHuntPackages + AddToQuickHuntDialogComponent.count >
      HUNT_PACKAGE_MAX
    )
  }

  public onSubmit(): void {
    this.huntService
      .addHuntPackagesToQuickHunt(this.hunt.value, this.generatedHuntPackages)
      .subscribe(
        (packagesAdded: HuntPackagesAdded) => {
          this.dialogRef.close(packagesAdded)
        },
        (error) => {
          this.error = error.message
        },
      )
  }

  public onClose(): void {
    this.dialogRef.close()
  }

  protected initializeOptions(): void {
    this.options = { headerText: 'Add To Quick Hunt' }
  }
}
